/**
 * Manages persistence of rejoin state in localStorage
 * Handles token expiration and validation
 */

interface RejoinState {
    roomId: string
    token: string
    expiresAt: number // timestamp
}

const STORAGE_KEY = "rejoinState"
const EXPIRY_DURATION = 24 * 60 * 60 * 1000 // 24 hours

const DRAFT_PREFIX = "draft_"

export const rejoinStorage = {
    /**
     * Save rejoin state with automatic expiration
     */
    save: (roomId: string, token: string): void => {
        const state: RejoinState = {
            roomId,
            token,
            expiresAt: Date.now() + EXPIRY_DURATION,
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    },

    /**
     * Save draft state for a specific room and round
     */
    saveDraft: (roomId: string, round: number, draft: string[]): void => {
        const key = `${DRAFT_PREFIX}${roomId}_${round}`;
        localStorage.setItem(key, JSON.stringify(draft));
    },

    /**
     * Retrieve draft state for a specific room and round
     */
    getDraft: (roomId: string, round: number): string[] | null => {
        const key = `${DRAFT_PREFIX}${roomId}_${round}`;
        const item = localStorage.getItem(key);
        if (!item) return null;

        try {
            return JSON.parse(item) as string[];
        } catch (error) {
            console.error("Failed to parse draft state:", error);
            localStorage.removeItem(key);
            return null;
        }
    },

    /**
     * Clear draft state for a specific room and round
     */
    clearDraft: (roomId: string, round: number): void => {
        const key = `${DRAFT_PREFIX}${roomId}_${round}`;
        localStorage.removeItem(key);
    },

    /**
     * Clear all drafts for this room (and older rooms) to prevent localStorage bloating
     */
    clearOldDrafts: (currentRoomId: string, currentRound: number): void => {
        try {
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith(DRAFT_PREFIX)) {
                    // Keep the current round's draft, delete everything else
                    if (key !== `${DRAFT_PREFIX}${currentRoomId}_${currentRound}`) {
                        keysToRemove.push(key);
                    }
                }
            }
            keysToRemove.forEach(k => localStorage.removeItem(k));
        } catch (e) {
            console.error("Failed to clear old drafts", e);
        }
    },

    /**
     * Get rejoin state if it exists and hasn't expired
     * Returns null if not found or expired (and clears expired state)
     */
    get: (): RejoinState | null => {
        const item = localStorage.getItem(STORAGE_KEY)
        if (!item) return null

        try {
            const state = JSON.parse(item) as RejoinState

            // Check expiration
            if (Date.now() > state.expiresAt) {
                rejoinStorage.clear()
                return null
            }

            return state
        } catch (error) {
            console.error("Failed to parse rejoin state:", error)
            rejoinStorage.clear()
            return null
        }
    },

    /**
     * Clear rejoin state
     */
    clear: (): void => {
        localStorage.removeItem(STORAGE_KEY)
    },

    /**
     * Check if rejoin state exists without retrieving it
     */
    exists: (): boolean => {
        return rejoinStorage.get() !== null
    },
}
